import { OpenAIOutlined } from '@ant-design/icons';
import { Checkbox, Modal } from 'antd';
import { useEffect, useState } from 'react';
import {
	BalloonGenerationOptions,
	BallooningSettings,
} from '../../interfaces/Ballooning';
import { useGetBallooningSettingsQuery } from '../../redux/api/ballooning';
import { viewportCappedModalStyles } from '../../utils/modalStyles';

const FALLBACK_OPTIONS: BalloonGenerationOptions = {
	balloon_gdt: true,
	auto_keyence: true,
};

const defaultsFrom = (
	settings: BallooningSettings | undefined
): BalloonGenerationOptions =>
	settings
		? {
				balloon_gdt: settings.default_balloon_gdt,
				auto_keyence: settings.default_auto_keyence,
		  }
		: FALLBACK_OPTIONS;

interface Props {
	open: boolean;
	loading?: boolean;
	onCancel: () => void;
	onGenerate: (options: BalloonGenerationOptions) => void;
}

/** Choices the user makes before the AI analyzes the drawing, starting from the shop defaults. */
const GenerationOptionsModal = ({
	open,
	loading,
	onCancel,
	onGenerate,
}: Props) => {
	const { data: settings } = useGetBallooningSettingsQuery();
	const [options, setOptions] = useState<BalloonGenerationOptions>(() =>
		defaultsFrom(settings)
	);

	useEffect(() => {
		if (open) setOptions(defaultsFrom(settings));
	}, [open, settings]);

	return (
		<Modal
			open={open}
			centered
			styles={viewportCappedModalStyles}
			width={420}
			title={
				<span className="flex items-center gap-2">
					<OpenAIOutlined />
					Generate Inspection Plan
				</span>
			}
			okText="Generate"
			onOk={() => onGenerate(options)}
			onCancel={onCancel}
			confirmLoading={loading}
		>
			<div className="flex flex-col gap-3 py-2">
				<div className="text-gray-500">
					Choose what the AI should balloon on the drawing. The defaults are set
					in Admin → Settings → Ballooning.
				</div>
				<Checkbox
					checked={options.balloon_gdt}
					onChange={(e) =>
						setOptions((o) => ({ ...o, balloon_gdt: e.target.checked }))
					}
					data-testid="generation-option-gdt"
				>
					<div className="font-medium">Balloon GD&amp;T</div>
					<div className="text-xs text-gray-500">
						Feature control frames (position, flatness, profile, …) and datum
						features.
					</div>
				</Checkbox>
				<Checkbox
					checked={options.auto_keyence}
					onChange={(e) =>
						setOptions((o) => ({ ...o, auto_keyence: e.target.checked }))
					}
					data-testid="generation-option-auto-keyence"
				>
					<div className="font-medium">
						Choose Keyence vs hand gage automatically
					</div>
					<div className="text-xs text-gray-500">
						Dimensions the Keyence can see in silhouette use the device mapped
						to this part&apos;s production method (Admin → Settings →
						Ballooning). Unchecked, every dimension is Hand Gage.
					</div>
				</Checkbox>
			</div>
		</Modal>
	);
};

export default GenerationOptionsModal;
